import React from 'react';
import toast from 'react-hot-toast';
import Switch from '../common/Switch';
import { useAuth } from '../../hooks/useAuth';
import { errorMessageFrom } from '../../api/client';
import { useUpdateMarketingConsent } from '../../services/marketingConsent';

const SWITCH_ID = 'marketing-consent-switch';
const FALLBACK_ERROR = 'Could not update your email preference. Please try again.';

/**
 * "Marketing emails" switch — where a signed-in person gives, withdraws and
 * re-gives their optional marketing consent. Withdrawing is exactly as easy as
 * giving: one click, no confirmation, effective immediately.
 *
 * The saved state lives on the session user (`user.marketing_opt_in`), so it is
 * updated from the server's answer rather than from local state. While a request
 * is in flight the switch shows the requested state and is disabled (no double
 * submit); if the request fails it snaps back and an error toast says so.
 *
 * Unlike the profile form beside it, this saves on click — there is no Save button.
 */
const MarketingConsentSwitch: React.FC = () => {
  const { user, updateUser } = useAuth();

  const update = useUpdateMarketingConsent({
    onSuccess: (state) => {
      if (user) updateUser({ ...user, marketing_opt_in: state.marketing_opt_in });
    },
    onError: (err) => {
      toast.error(errorMessageFrom(err) ?? FALLBACK_ERROR);
    },
  });

  const saved = user?.marketing_opt_in;
  // Unknown (older backend, or no user yet): never claim "on", and never let a
  // click guess at the current state.
  const known = typeof saved === 'boolean';
  const checked = update.isPending ? Boolean(update.variables) : saved === true;

  return (
    <section
      aria-labelledby="marketing-consent-heading"
      className="bg-surface-primary rounded-2xl shadow-card p-6"
    >
      <h3 id="marketing-consent-heading" className="text-sm font-bold text-text-primary">
        Email preferences
      </h3>
      <div className="mt-4 flex items-center justify-between gap-4">
        <div className="min-w-0">
          <label htmlFor={SWITCH_ID} className="text-sm font-medium text-text-primary">
            Marketing emails
          </label>
          <p className="mt-0.5 text-xs text-text-secondary">
            Product news and learning content from Tereiko. You can change this at any time; it
            doesn&apos;t affect account or course notifications.
          </p>
        </div>
        <Switch
          id={SWITCH_ID}
          aria-label="Marketing emails"
          checked={checked}
          disabled={!user || !known || update.isPending}
          onChange={(next) => update.mutate(next)}
        />
      </div>
    </section>
  );
};

export default MarketingConsentSwitch;
